/* eslint-disable */
/**
 * BulkRoomBuilder — Add multiple rooms at once
 *
 * Interactive row-based builder (like proposal room builder).
 * Supports:
 *  - Manual row-by-row entry (room number, name, floor, bed count, type)
 *  - "Generate Range" helper (e.g. 101–110) to auto-fill numbered rows
 *  - Bulk insert to hosp_rooms on save
 */

import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

const ROOM_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'deluxe', label: 'Deluxe' },
  { value: 'suite', label: 'Suite' },
  { value: 'family', label: 'Family' },
  { value: 'twin', label: 'Twin' },
  { value: 'dormitory', label: 'Dormitory' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'other', label: 'Other' },
];

interface RoomRow {
  room_number: string;
  room_name: string;
  floor: string;
  bed_count: number;
  room_type: string;
}

interface BulkRoomBuilderProps {
  property: Record<string, any>;
  supabase: SupabaseClient;
  onClose: () => void;
  onSave: () => Promise<void>;
}

const EMPTY_ROW: RoomRow = { room_number: '', room_name: '', floor: '', bed_count: 1, room_type: 'standard' };

export default function BulkRoomBuilder({ property, supabase, onClose, onSave }: BulkRoomBuilderProps) {
  const [rows, setRows] = useState<RoomRow[]>([{ ...EMPTY_ROW }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Range generator state
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [rangeFloor, setRangeFloor] = useState('');
  const [rangeType, setRangeType] = useState('standard');
  const [rangeBeds, setRangeBeds] = useState(1);

  const addRow = () => setRows(r => [...r, { ...EMPTY_ROW }]);

  const updateRow = (index: number, field: keyof RoomRow, value: string | number) => {
    setRows(r => r.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const removeRow = (index: number) => {
    setRows(r => r.filter((_row, i) => i !== index));
  };

  const generateRange = () => {
    const from = parseInt(rangeFrom);
    const to = parseInt(rangeTo);
    if (isNaN(from) || isNaN(to) || from > to || to - from > 500) {
      setError('Enter a valid range (max 500 rooms)');
      return;
    }
    setError(null);

    const newRows: RoomRow[] = [];
    for (let i = from; i <= to; i++) {
      newRows.push({
        room_number: String(i),
        room_name: `Room ${i}`,
        floor: rangeFloor || '',
        bed_count: rangeBeds || 1,
        room_type: rangeType || 'standard',
      });
    }

    // Replace empty rows or append
    setRows(prev => {
      const nonEmpty = prev.filter(r => r.room_number.trim() || r.room_name.trim());
      return [...nonEmpty, ...newRows];
    });

    // Reset range inputs
    setRangeFrom('');
    setRangeTo('');
  };

  const handleSave = async () => {
    // Filter out empty rows
    const validRows = rows.filter(r => r.room_number.trim() || r.room_name.trim());

    if (validRows.length === 0) {
      setError('Add at least one room');
      return;
    }

    // Validate room numbers
    const missingNumbers = validRows.some(r => !r.room_number.trim());
    if (missingNumbers) {
      setError('Every room needs a room number');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const insertData = validRows.map(r => ({
        property_id: property.id,
        room_number: r.room_number.trim(),
        room_name: r.room_name.trim() || r.room_number.trim(),
        floor: r.floor.trim() || null,
        bed_count: r.bed_count || 1,
        room_type: r.room_type || 'standard',
        status: 'active',
      }));

      const { error: insertError } = await supabase
        .from('hosp_rooms')
        .insert(insertData);

      if (insertError) throw insertError;

      await onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const validCount = rows.filter(r => r.room_number.trim() || r.room_name.trim()).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">Bulk Add Rooms — {property.property_name}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
          {/* Range Generator */}
          <div style={{
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius)',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            border: '1px solid var(--color-border)'
          }}>
            <div style={{ fontSize: 'var(--text-md)', fontWeight: 600, marginBottom: '0.5rem' }}>
              Generate Room Range
            </div>
            <div className="flex gap-sm items-end flex-wrap">
              <div>
                <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', display: 'block' }}>From</label>
                <input
                  type="number"
                  className="form-input"
                  value={rangeFrom}
                  onChange={e => setRangeFrom(e.target.value)}
                  placeholder="101"
                  style={{ width: '80px', height: '32px', fontSize: 'var(--text-base)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', display: 'block' }}>To</label>
                <input
                  type="number"
                  className="form-input"
                  value={rangeTo}
                  onChange={e => setRangeTo(e.target.value)}
                  placeholder="110"
                  style={{ width: '80px', height: '32px', fontSize: 'var(--text-base)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', display: 'block' }}>Floor</label>
                <input
                  type="text"
                  className="form-input"
                  value={rangeFloor}
                  onChange={e => setRangeFloor(e.target.value)}
                  placeholder="1"
                  style={{ width: '60px', height: '32px', fontSize: 'var(--text-base)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', display: 'block' }}>Beds</label>
                <input
                  type="number"
                  className="form-input"
                  value={rangeBeds}
                  onChange={e => setRangeBeds(parseInt(e.target.value) || 1)}
                  min="1"
                  style={{ width: '60px', height: '32px', fontSize: 'var(--text-base)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', display: 'block' }}>Type</label>
                <select
                  className="form-select"
                  value={rangeType}
                  onChange={e => setRangeType(e.target.value)}
                  style={{ height: '32px', fontSize: 'var(--text-base)' }}
                >
                  {ROOM_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={generateRange}
                style={{ height: '32px', padding: '0 0.75rem', fontSize: 'var(--text-base)' }}
                disabled={!rangeFrom || !rangeTo}
              >
                Generate
              </button>
            </div>
          </div>

          {/* Room Rows */}
          <div style={{ marginBottom: '0.5rem', fontSize: 'var(--text-md)', fontWeight: 600 }}>
            Rooms ({validCount})
          </div>

          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            overflow: 'hidden'
          }}>
            <div className="mobile-table-scroll">
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '100px 1fr 80px 70px 120px 40px', minWidth: '600px',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: 'var(--color-bg-secondary)',
              borderBottom: '1px solid var(--color-border)',
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: 'var(--color-text-light)'
            }}>
              <span>Room #</span>
              <span>Name</span>
              <span>Floor</span>
              <span>Beds</span>
              <span>Type</span>
              <span></span>
            </div>

            {/* Rows */}
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 1fr 80px 70px 120px 40px', minWidth: '600px',
                    gap: '0.5rem',
                    padding: '0.35rem 0.75rem',
                    borderBottom: idx < rows.length - 1 ? '1px solid var(--color-border-light, #eee)' : 'none',
                    alignItems: 'center'
                  }}
                >
                  <input
                    type="text"
                    className="form-input"
                    value={row.room_number}
                    onChange={e => updateRow(idx, 'room_number', e.target.value)}
                    placeholder="101"
                    style={{ height: '30px', fontSize: 'var(--text-md)' }}
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={row.room_name}
                    onChange={e => updateRow(idx, 'room_name', e.target.value)}
                    placeholder="Room name"
                    style={{ height: '30px', fontSize: 'var(--text-md)' }}
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={row.floor}
                    onChange={e => updateRow(idx, 'floor', e.target.value)}
                    placeholder="1"
                    style={{ height: '30px', fontSize: 'var(--text-md)' }}
                  />
                  <input
                    type="number"
                    className="form-input"
                    value={row.bed_count}
                    onChange={e => updateRow(idx, 'bed_count', parseInt(e.target.value) || 1)}
                    min="1"
                    style={{ height: '30px', fontSize: 'var(--text-md)' }}
                  />
                  <select
                    className="form-select"
                    value={row.room_type}
                    onChange={e => updateRow(idx, 'room_type', e.target.value)}
                    style={{ height: '30px', fontSize: 'var(--text-md)' }}
                  >
                    {ROOM_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-error)',
                      cursor: 'pointer',
                      fontSize: 'var(--text-xl)',
                      padding: '0',
                      lineHeight: 1
                    }}
                    title="Remove row"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={addRow}
            style={{ marginTop: '0.5rem', fontSize: 'var(--text-md)' }}
          >
            + Add Row
          </button>

          {error && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: 'var(--color-error-bg, #fee2e2)',
              color: 'var(--color-error)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-base)'
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer flex justify-between items-center">
          <span style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
            {validCount} room{validCount !== 1 ? 's' : ''} to add
          </span>
          <div className="flex gap-sm">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || validCount === 0}
            >
              {saving ? 'Saving...' : `Add ${validCount} Room${validCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
